import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import dayjs from "dayjs";
import { IconX } from "./icons";

/** Collapse all / Expand all: every panel on the page listens. */
export const FOLD_EVENT = "rca:fold";
const foldKey = (title: ReactNode, collapse?: string) => {
  if (collapse) return `rca.panel.${collapse}`;
  if (typeof title !== "string") return "";
  const t = title.replace(/\(?\d[\d,.$ ]*\)?/g, "").replace(/\s+/g, " ").trim().toLowerCase();
  return t ? `rca.panel.${location.pathname}:${t}` : "";
};
export function Panel({ title, hint, actions, children, className, collapse, closed, fixed }: { title?: ReactNode; hint?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string;
  /** a key to remember it by (titled panels fold anyway); `closed` = folded the first time; `fixed` = can't fold */ collapse?: string; closed?: boolean; fixed?: boolean }) {
  const key = fixed ? "" : foldKey(title, collapse);
  const [shut, setShut] = useState<boolean>(() => {
    if (!key) return false;
    try { const v = localStorage.getItem(key); return v == null ? !!closed : v === "1"; } catch { return !!closed; }
  });
  const put = useCallback((v: boolean) => { setShut(v); try { localStorage.setItem(key, v ? "1" : "0"); } catch { /* fine */ } }, [key]);
  useEffect(() => {
    if (!key) return;
    const on = (e: Event) => put(!!(e as CustomEvent).detail);
    window.addEventListener(FOLD_EVENT, on);
    return () => window.removeEventListener(FOLD_EVENT, on);
  }, [key, put]);
  const toggle = () => put(!shut);
  const can = !!key;
  return (
    <section className={clsx("panel", className, shut && "panel-shut")} data-foldable={can || undefined}>
      {(title || actions) && (
        <div className="panel-head">
          <div className={can ? "panel-toggle" : undefined} onClick={can ? toggle : undefined} role={can ? "button" : undefined}
            aria-expanded={can ? !shut : undefined} tabIndex={can ? 0 : undefined} onKeyDown={can ? (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), toggle()) : undefined}>
            {title && <h2>{can && <span className="panel-caret" aria-hidden>{shut ? "▸" : "▾"}</span>}{title}</h2>}
            {hint && !shut && <div className="hint">{hint}</div>}
          </div>
          {!shut && actions}
        </div>
      )}
      {!shut && children}
    </section>
  );
}

/** One button for the page: fold every panel, or open them all again. */
export function FoldAll() {
  const [n, setN] = useState(0);
  useEffect(() => { const t = setTimeout(() => setN(document.querySelectorAll("[data-foldable]").length), 300); return () => clearTimeout(t); });
  if (n < 2) return null;
  const fire = (v: boolean) => window.dispatchEvent(new CustomEvent(FOLD_EVENT, { detail: v }));
  return <span className="fold-all"><button className="linkish small" onClick={() => fire(true)}>Collapse all</button><span className="muted"> · </span><button className="linkish small" onClick={() => fire(false)}>Expand all</button></span>;
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return <div className="empty"><b>{title}</b>{children}</div>;
}

export function ErrorBox({ error }: { error: Error | null | undefined }) {
  if (!error) return null;
  return <div className="error-box" role="alert">{error.message}</div>;
}

export function Loading({ rows = 4 }: { rows?: number }) {
  return (
    <div style={{ display: "grid", gap: 10, padding: 16 }} aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => <div key={i} className="skeleton" style={{ width: `${90 - i * 12}%` }} />)}
    </div>
  );
}

export function Field({ label, children, wide }: { label: string; children: ReactNode; wide?: boolean }) {
  return <label className={clsx("field", wide && "wide")}><span>{label}</span>{children}</label>;
}

export function Select({ value, onChange, options, placeholder, ...rest }: {
  value: string | null | undefined; onChange: (v: string) => void; options: (string | { value: string; label: string })[]; placeholder?: string;
} & Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "onChange" | "value">) {
  return (
    <select className="select" value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...rest}>
      <option value="">{placeholder ?? "—"}</option>
      {options.map((o) => typeof o === "string"
        ? <option key={o} value={o}>{o}</option>
        : <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

export function Seg<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { value: T; label: string; n?: number }[] }) {
  return (
    <div className="seg" role="group">
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>
          {o.label}{o.n !== undefined && <span className="n">({o.n})</span>}
        </button>
      ))}
    </div>
  );
}

export function Drawer({ onClose, children, label }: { onClose: () => void; children: ReactNode; label: string }) {
  useEffect(() => {
    const on = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [onClose]);
  return createPortal(
    <>
      <div className="scrim" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-label={label}>{children}</aside>
    </>,
    document.body,
  );
}

export function Sheet({ title, onClose, children, footer }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  useEffect(() => {
    const on = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [onClose]);
  return createPortal(
    <>
      <div className="scrim" onClick={onClose} />
      <div className="sheet" role="dialog" aria-label={title}>
        <div className="sheet-head">
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><IconX /></button>
        </div>
        <div className="sheet-body">{children}</div>
        {footer && <div className="sheet-foot">{footer}</div>}
      </div>
    </>,
    document.body,
  );
}

// ───── toasts ─────
const ToastCtx = createContext<(msg: string) => void>(() => undefined);
export function ToastProvider({ children }: { children: ReactNode }) {
  const [msg, setMsg] = useState<string | null>(null);
  const show = useCallback((m: string) => {
    setMsg(m);
    window.setTimeout(() => setMsg((cur) => (cur === m ? null : cur)), 2600);
  }, []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      {msg && <div className="toast" role="status">{msg}</div>}
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);

// ───── formatting ─────
export const fmtDate = (d?: string | null, f = "MMM D, YYYY") => (d ? dayjs(d).format(f) : "");
export const fmtShort = (d?: string | null) => (d ? dayjs(d).format(dayjs(d).year() === dayjs().year() ? "MMM D" : "MMM D, YYYY") : "");
export const daysAgo = (d?: string | null) => {
  if (!d) return "never";
  const n = dayjs().startOf("day").diff(dayjs(d), "day");
  return n === 0 ? "today" : n === 1 ? "yesterday" : n < 0 ? `in ${-n} days` : `${n} days ago`;
};
export const money = (n?: number | null) => (n == null ? "" : n.toLocaleString("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }));
/** Money with cents (premiums, contributions): $96.40 */
export const cents = (n?: number | string | null) => (n == null || n === "" ? "" : Number(n).toLocaleString("en-CA", { style: "currency", currency: "CAD", minimumFractionDigits: 2, maximumFractionDigits: 2 }));
/** "the 1st", "the 22nd" */
export const ordinal = (d: number) => `${d}${d % 10 === 1 && d !== 11 ? "st" : d % 10 === 2 && d !== 12 ? "nd" : d % 10 === 3 && d !== 13 ? "rd" : "th"}`;

export function Temperature({ t }: { t?: string | null }) {
  if (!t) return null;
  return <span className={clsx("pill", t.toLowerCase())}><i className="dot-i" />{t}</span>;
}

export function WorkspaceBadges({ team, lead, client }: { team?: boolean; lead?: boolean; client?: boolean }) {
  return (
    <>
      {team && <span className="pill team">Team</span>}
      {lead && <span className="pill leads">Lead</span>}
      {client && <span className="pill clients">Client</span>}
    </>
  );
}
